<template>
  <v-dialog v-model="download.running" :persistent="true" width="70vw">
    <v-card>
      <v-card-title>{{ title }}</v-card-title>
      <v-card-text>
        <v-list>
          <v-list-item
            v-for="([stepIcon, stepText], index) in Object.entries(steps)"
            :key="`computingStep_${index}`"
            :active="isDownloading || isComputing(index)"
            :disabled="!isDownloading && index > state.step"
          >
            <v-list-item-icon class="me-2">
              <v-icon size="22">
                {{ icons.computingSteps[stepIcon] }}
              </v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title>
                {{ stepText
                }}{{
                  isComputing(index) ? "..." : state.step > index ? "." : ""
                }}
                <loader v-if="isComputing(index)" :showText="false" />
                <v-icon
                  color="success"
                  v-else-if="isDownloading || index < state.step"
                  >{{ icons.mdiCheck }}</v-icon
                >
              </v-list-item-title>
            </v-list-item-content>
          </v-list-item>

          <v-list-item
            key="downloading"
            :active="isDownloading"
            :disabled="!isDownloading"
            v-if="showDownloadState"
          >
            <v-list-item-icon class="me-2">
              <v-icon size="22">
                {{ icons.mdiFileDownloadOutline }}
              </v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title>
                Transfert des données...
                <v-progress-linear
                  v-model="download.percentCompleted"
                  :height="height"
                  v-if="isDownloading"
                >
                  <template v-slot:default="{ value }">
                    <strong class="textWhite">{{ Math.ceil(value) }}%</strong>
                  </template>
                </v-progress-linear>
              </v-list-item-title>
            </v-list-item-content>
          </v-list-item>
        </v-list>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import {
  mdiCalculatorVariantOutline,
  mdiCheck,
  mdiDatabaseArrowLeftOutline,
  mdiFileDownloadOutline,
  mdiFileImportOutline,
  mdiMerge,
  mdiPaletteSwatchVariant,
  mdiSendClockOutline,
  // Veille Globale : via props.steps
  mdiCardSearchOutline,
  mdiEarth,
  mdiNewspaperVariantOutline,
  // AI Factory : via props.steps
  mdiBookOpenVariant,
  mdiBrain,
} from "@mdi/js";
import {
  computed,
  del,
  onMounted,
  reactive,
  set,
  watch,
} from "@vue/composition-api";

import Loader from "@/components/common/Loader";

export const download_initial_state = () => ({
  running: false,
  percentCompleted: 0,
});

export const resetDownload = (downloadState) => {
  const initialState = download_initial_state();
  for (const [key, value] of Object.entries(initialState)) {
    set(downloadState, key, value);
  }
  for (const key of Object.keys(downloadState)) {
    if (!(key in initialState)) {
      del(downloadState, key);
    }
  }
};

const STEP_DURATION = 5000;
const ACCELERATED_STEP_DURATION = 100;

const STEPS = {
  mdiDatabaseArrowLeftOutline: "Récupération des infos en base de données",
  mdiCalculatorVariantOutline: "Exécution des calculs",
  mdiMerge: "Agrégation des résultats",
  mdiFileImportOutline: "Génération du fichier",
  mdiPaletteSwatchVariant: "Application des styles",
  mdiSendClockOutline: "Finalisation de l'envoi",
};

export default {
  name: "ProgressBar",
  components: {
    Loader,
  },
  props: {
    title: {
      type: String,
      default: "Téléchargement...",
    },
    height: {
      type: Number,
      default: 25,
    },
    showDownloadState: {
      type: Boolean,
      default: true,
    },
    download: {
      type: Object,
      required: true,
      validator(value) {
        return (
          "running" in value &&
          "percentCompleted" in value &&
          typeof value.running === "boolean" &&
          !isNaN(parseFloat(value.percentCompleted))
        );
      },
    },
    stepDuration: {
      type: Number,
      default: STEP_DURATION,
      validator(value) {
        return value >= 1000 && value <= 10000;
      },
    },
    steps: {
      type: Object,
      default: () => STEPS,
      validator(value) {
        return Object.entries(value).every(
          ([key, text]) =>
            typeof key === "string" &&
            key.startsWith("mdi") &&
            typeof text === "string" &&
            text.length > 0
        );
      },
    },
  },
  setup(props, { emit }) {
    const state = reactive({
      step: 0,
      // timer: null,
      stepDuration: props.stepDuration,
    });

    // =>  METHODS
    const resetComponent = () => {
      del(state, "timer");
      state.step = 0;
      state.stepDuration = isDownloading.value
        ? ACCELERATED_STEP_DURATION
        : props.stepDuration;
    };

    const scheduleNextStep = () => {
      set(
        state,
        "timer",
        setTimeout(() => {
          runNextStep();
        }, state.stepDuration)
      );
    };

    const runNextStep = () => {
      if (state.step >= nbSteps.value - 1) {
        return;
      }

      state.step += 1;
      scheduleNextStep();
    };

    const isComputing = (step) => {
      return !isDownloading.value && step === state.step;
    };

    // =>  COMPUTED
    const nbSteps = computed(() => Object.keys(props.steps).length);
    const isDownloading = computed(() => props.download.percentCompleted > 0);

    // =>  WATCH
    watch(
      () => props.download.running,
      (newVal) => {
        if (newVal !== true) {
          return;
        }

        resetComponent();
        scheduleNextStep();
      }
    );

    watch(
      () => isDownloading.value,
      (newVal) => {
        if (newVal !== true) {
          return;
        }

        del(state, "timer");
        state.stepDuration = ACCELERATED_STEP_DURATION;
        runNextStep();
      }
    );

    // =>  HOOKS
    onMounted(() => {
      resetComponent();
    });

    // =>  DATA
    return {
      state,
      isDownloading,
      isComputing,
      icons: {
        computingSteps: {
          mdiDatabaseArrowLeftOutline,
          mdiCalculatorVariantOutline,
          mdiMerge,
          mdiFileImportOutline,
          mdiPaletteSwatchVariant,
          mdiSendClockOutline,
          // Veille Globale : via props.steps
          mdiCardSearchOutline,
          mdiEarth,
          mdiNewspaperVariantOutline,
          // AI Factory : via props.steps
          mdiBookOpenVariant,
          mdiBrain,
        },
        mdiCheck,
        mdiFileDownloadOutline,
      },
      dialogModel: {
        dialog: props.download.running,
      },
    };
  },
};
</script>

<style scoped>
.textWhite {
  color: #fff;
}
</style>
